<template>
  <div class="sci-view-container">
    <!-- Top Glassmorphic Navigation -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-users-cog me-2 text-primary"></i>
            {{ $t('gestioneCredenziali') || 'Gestione Utenze & Ruoli Operatori' }}
          </h1>
          <p class="sci-subtitle">Crea account per il personale di sala, cucina e cassa con permessi modulari</p>
        </div>
      </div>
      <div class="sci-header-right">
        <button class="sci-btn sci-btn-primary" @click="addAccount">
          <i class="fas fa-user-plus me-1"></i>
          <span>Nuovo Operatore</span>
        </button>
      </div>
    </header>

    <!-- Floating Feedback Toast -->
    <transition name="fade">
      <div class="sci-floating-toast" v-if="feedbackMsg">
        <i class="fas fa-check-circle text-success me-2"></i>
        <span>{{ feedbackMsg }}</span>
      </div>
    </transition>

    <!-- Main Spatial Bento Grid -->
    <main class="sci-main-content">
      <!-- KPI Stats -->
      <section class="kpi-bento-grid mb-4">
        <div class="sci-card kpi-card">
          <div class="kpi-icon-box users-icon">
            <i class="fas fa-user-shield"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Account Attivi</span>
            <span class="kpi-number">{{ utenti.length }}</span>
            <span class="kpi-subtext text-muted">Operatori abilitati all'accesso</span>
          </div>
        </div>

        <div class="sci-card kpi-card">
          <div class="kpi-icon-box perm-icon">
            <i class="fas fa-key"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Permessi di Sistema</span>
            <span class="kpi-number">{{ permessi.length }}</span>
            <span class="kpi-subtext text-muted">Moduli assegnabili individualmente</span>
          </div>
        </div>

        <div class="sci-card kpi-card">
          <div class="kpi-icon-box security-icon">
            <i class="fas fa-lock"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Sicurezza Accessi</span>
            <span class="kpi-number text-success">Crittografato</span>
            <span class="kpi-subtext text-muted">Autenticazione protetta con JWT</span>
          </div>
        </div>
      </section>

      <!-- Users Grid -->
      <section class="users-grid">
        <div 
          v-for="(utente, i) in utenti" 
          :key="utente.id || i" 
          class="sci-card user-card"
        >
          <!-- Card Header -->
          <div class="user-card-header">
            <div class="user-profile-group">
              <div class="user-avatar" :style="{ backgroundColor: getUserAvatarColor(utente.nome) }">
                {{ getUserInitial(utente.nome) }}
              </div>
              <div class="user-title-wrap">
                <h3 class="user-display-name">
                  {{ utente.nome || 'Nuovo Utente (Da Configurare)' }}
                </h3>
                <span class="user-roles-count">
                  <i class="fas fa-shield-alt me-1"></i>
                  {{ utente.permessi && utente.permessi.length ? utente.permessi.length + ' permessi attivi' : 'Nessun permesso assegnato' }}
                </span>
              </div>
            </div>

            <button 
              class="btn-delete-user" 
              title="Elimina Operatore" 
              @click="action(i, utente.id, utente.nome, utente.password, utente.permessi, false)"
            >
              <i class="fas fa-trash-alt"></i>
            </button>
          </div>

          <!-- Card Form Body -->
          <div class="user-form-body">
            <div class="form-row-grid">
              <!-- Username -->
              <div class="sci-input-group">
                <label class="sci-label">Nome Utente / Login</label>
                <div class="sci-input-wrap">
                  <i class="fas fa-user input-icon"></i>
                  <input 
                    type="text" 
                    class="sci-input" 
                    placeholder="Es. mario.rossi o cameriere1" 
                    v-model="utente.nome"
                  />
                </div>
              </div>

              <!-- Password -->
              <div class="sci-input-group">
                <label class="sci-label">Password di Accesso</label>
                <div class="sci-input-wrap">
                  <i class="fas fa-lock input-icon"></i>
                  <input 
                    :type="showPassword[i] ? 'text' : 'password'" 
                    class="sci-input font-monospace" 
                    placeholder="••••••••" 
                    v-model="utente.password"
                  />
                  <button 
                    type="button" 
                    class="btn-toggle-pw" 
                    @click="togglePwVisibility(i)"
                    title="Mostra / Nascondi Password"
                  >
                    <i class="fas" :class="showPassword[i] ? 'fa-eye-slash' : 'fa-eye'"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- Permessi Multiselect -->
            <div class="sci-input-group mt-3">
              <label class="sci-label">Moduli & Funzionalità Abilitate</label>
              <div class="multiselect-wrap">
                <Multiselect
                  v-model="utente.permessi"
                  :options="permessi"
                  mode="tags"
                  :close-on-select="false"
                  placeholder="Seleziona i permessi (es. Ordina, Cassa, Magazzino)..."
                  class="sci-multiselect"
                />
              </div>
            </div>
          </div>

          <!-- Card Footer Action -->
          <div class="user-card-footer">
            <div class="footer-tags" v-if="utente.permessi && utente.permessi.length">
              <span class="badge-role">
                <i class="fas fa-check-circle text-success me-1"></i>
                Configurazione Completa
              </span>
            </div>
            <div class="footer-tags" v-else>
              <span class="badge-role role-warning">
                <i class="fas fa-exclamation-triangle text-warning me-1"></i>
                Assegna almeno un modulo
              </span>
            </div>

            <button 
              class="sci-btn sci-btn-success ms-auto" 
              @click="action(i, utente.id, utente.nome, utente.password, utente.permessi, true)"
              :disabled="!utente.nome || !utente.password"
            >
              <i class="fas fa-save me-1"></i>
              <span>Salva Operatore</span>
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div class="sci-card empty-card" v-if="utenti.length === 0">
          <div class="empty-icon-halo">
            <i class="fas fa-user-friends"></i>
          </div>
          <h3 class="empty-title">Nessun operatore registrato</h3>
          <p class="empty-desc">
            Crea il primo account operatore per consentire al tuo staff di accedere con ruoli e permessi dedicati.
          </p>
          <button class="sci-btn sci-btn-primary mt-3" @click="addAccount">
            <i class="fas fa-user-plus me-1"></i> Aggiungi Operatore Ora
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import Multiselect from '@vueform/multiselect'
import { store } from '../store/store.js'

export default {
  name: 'GestioneCredenziali',
  components: {
    Multiselect
  },
  data() {
    return {
      permessi: [],
      utenti: [],
      showPassword: {},
      feedbackMsg: '',
      store
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    togglePwVisibility(index) {
      this.showPassword[index] = !this.showPassword[index];
    },
    getUserInitial(name) {
      if (!name) return '?';
      return name.trim().charAt(0).toUpperCase();
    },
    getUserAvatarColor(name) {
      if (!name) return '#64748b';
      const colors = ['#4f46e5', '#059669', '#d97706', '#0284c7', '#7c3aed', '#db2777', '#0891b2'];
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % colors.length;
      return colors[index];
    },
    addAccount() {
      this.utenti.unshift({ nome: '', password: '', permessi: [] });
      this.showToast('Nuovo operatore inserito in cima');
    },
    displayPermessi() {
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.post(this.store.path + 'displayPermessi', {
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          const list = [];
          response.data.messaggio.forEach(element => {
            list.push({
              value: element._id,
              label: element.label || element.event || element._id
            });
          });
          this.permessi = list;
        }
      }).catch(err => {
        console.error("Errore caricamento permessi:", err);
      });
    },
    getusers() {
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.post(this.store.path + 'displayUsers', {
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          const list = [];
          response.data.messaggio.forEach(element => {
            list.push({
              id: element._id || element.id,
              nome: element.nome,
              password: element.password,
              permessi: Array.isArray(element.permessi) ? element.permessi : []
            });
          });
          this.utenti = list;
        }
      }).catch(err => {
        console.error("Errore caricamento utenti:", err);
      });
    },
    action(ind, user, nome, pw, permessi, save) {
      if (!save) {
        if (!confirm(`Sei sicuro di voler eliminare l'account operatore "${nome || 'Senza Nome'}"?`)) {
          return;
        }
      }

      var path = save ? 'login' : 'deleteCredential';
      if (!save) {
        this.utenti.splice(ind, 1);
      }

      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.post(this.store.path + path, {
        nome: nome,
        password: pw,
        permessi: permessi,
        admin: true,
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        if (response.data && response.data.messaggio) {
          this.showToast(save ? 'Account operatore salvato' : 'Account eliminato con successo');
          if (save) {
            this.getusers();
          }
        } else {
          this.showToast('Operazione completata');
        }
      }).catch(err => {
        console.error("Errore salvataggio credenziali:", err);
        alert('Si è verificato un errore durante l\'operazione');
      });
    },
    showToast(msg) {
      this.feedbackMsg = msg;
      setTimeout(() => {
        if (this.feedbackMsg === msg) this.feedbackMsg = '';
      }, 2500);
    }
  },
  mounted() {
    this.displayPermessi();
    this.getusers();
  }
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 60px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 12px;
  color: var(--sci-text-muted, #475569);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  color: var(--sci-text-main, #0f172a);
  border-color: #cbd5e1;
}

.sci-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.82rem;
  color: var(--sci-text-muted, #475569);
}

.sci-btn-primary {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
  padding: 0 20px;
  background: var(--sci-primary, #4f46e5);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sci-btn-primary:hover {
  background: #4338ca;
}

.sci-btn-success {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 18px;
  background: #059669;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.sci-btn-success:hover:not(:disabled) {
  background: #047857;
}

.sci-btn-success:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.sci-main-content {
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 24px 20px;
}

/* Card */
.sci-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 18px;
  box-shadow: 0 4px 6px -1px rgba(15, 23, 42, 0.05);
  padding: 22px;
}

/* KPI Grid */
.kpi-bento-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
}

.kpi-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
  flex-shrink: 0;
}

.users-icon { background: #eef2ff; color: #4f46e5; }
.perm-icon { background: #fffbeb; color: #d97706; }
.security-icon { background: #ecfdf5; color: #059669; }

.kpi-info {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--sci-text-muted, #475569);
  letter-spacing: 0.4px;
}

.kpi-number {
  font-size: 1.45rem;
  font-weight: 800;
  margin: 3px 0 2px 0;
}

.kpi-subtext {
  font-size: 0.78rem;
}

/* Users Grid */
.users-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.user-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s;
}

.user-card:hover {
  box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.08);
}

.user-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 14px;
}

.user-profile-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.user-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  color: #ffffff;
  font-weight: 800;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.user-title-wrap {
  display: flex;
  flex-direction: column;
}

.user-display-name {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
}

.user-roles-count {
  font-size: 0.8rem;
  color: var(--sci-text-muted, #475569);
}

.btn-delete-user {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid #fee2e2;
  background: #fff5f5;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-delete-user:hover {
  background: #ef4444;
  color: #ffffff;
  border-color: #ef4444;
}

.form-row-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 768px) {
  .form-row-grid {
    grid-template-columns: 1fr;
  }
}

.sci-input-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sci-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--sci-text-muted, #475569);
}

.sci-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 14px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.sci-input {
  width: 100%;
  height: 42px;
  padding: 0 40px 0 38px;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 10px;
  font-size: 0.9rem;
  color: var(--sci-text-main, #0f172a);
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s;
}

.sci-input:focus {
  border-color: var(--sci-primary, #4f46e5);
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.btn-toggle-pw {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 6px;
  font-size: 0.9rem;
}

.btn-toggle-pw:hover {
  color: var(--sci-text-main, #0f172a);
}

.user-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f1f5f9;
  padding-top: 14px;
}

.badge-role {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
  background: #ecfdf5;
  color: #065f46;
}

.badge-role.role-warning {
  background: #fffbeb;
  color: #b45309;
}

/* Empty State */
.empty-card {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon-halo {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #eef2ff;
  color: var(--sci-primary, #4f46e5);
  font-size: 1.8rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px auto;
}

.empty-title {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 6px;
}

.empty-desc {
  font-size: 0.88rem;
  color: var(--sci-text-muted, #475569);
  max-width: 480px;
  margin: 0 auto;
}

/* Floating Toast */
.sci-floating-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  background: #0f172a;
  color: #ffffff;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  font-weight: 600;
  font-size: 0.9rem;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
